import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ReCaptcha } from '@/components/ReCaptcha';
import ReCAPTCHA from 'react-google-recaptcha';

export const LeadMagnetForm = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', company: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      toast({
        title: language === 'en' ? "Error" : "Error",
        description: language === 'en' 
          ? "Please complete the reCAPTCHA verification." 
          : "Por favor completa la verificación reCAPTCHA.",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);

    try {
      const data = {
        type: 'lead-magnet' as const,
        name: formData.name,
        email: formData.email,
        company: formData.company || undefined,
        language: language,
        recaptchaToken: recaptchaToken
      };

      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: data
      });

      if (error) throw error;

      toast({
        title: language === 'en' ? "Request Sent!" : "¡Solicitud Enviada!",
        description: language === 'en' 
          ? "We'll send your checklist to your email shortly!" 
          : "¡Te enviaremos tu lista a tu email pronto!",
      });

      // Reset form
      setFormData({ name: '', email: '', company: '' });
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
      
    } catch (error) {
      console.error('Lead magnet form error:', error);
      toast({
        title: language === 'en' ? "Error" : "Error",
        description: language === 'en' 
          ? "There was an error processing your request. Please try again." 
          : "Hubo un error procesando tu solicitud. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    }
    
    setIsLoading(false);
  };

  return (
    <Card className="w-full max-w-md shadow-medium">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
          <Download className="w-6 h-6 text-success" />
        </div>
        <CardTitle className="text-lg">
          {language === 'en' ? 'Get Your Free Pricing Strategy Checklist' : 'Obtén Tu Lista Gratuita de Estrategia de Precios'}
        </CardTitle>
        <CardDescription>
          {language === 'en' ? 'Enter your details below to receive your free checklist' : 'Ingresa tus datos para recibir tu lista gratuita'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">{language === 'en' ? 'Name' : 'Nombre'}</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="John Doe"
            />
          </div>
          <div>
            <Label htmlFor="email">{language === 'en' ? 'Email' : 'Email'}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="john@company.com"
            />
          </div>
          <div>
            <Label htmlFor="company">{language === 'en' ? 'Company' : 'Empresa'}</Label>
            <Input
              id="company"
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Your Company"
            />
          </div>
          
          <ReCaptcha
            ref={recaptchaRef}
            onVerify={setRecaptchaToken}
            onExpired={() => setRecaptchaToken(null)}
            onError={() => setRecaptchaToken(null)}
          />
          
          <Button type="submit" className="w-full" disabled={isLoading || !recaptchaToken}>
            {isLoading ? (language === 'en' ? 'Sending...' : 'Enviando...') : (language === 'en' ? 'Get My Free Checklist' : 'Obtener Mi Lista Gratuita')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};